import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { Button } from '@/components/ui/button';
import { canOfferAct } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import type {
  AgentBroker,
  AgentBrokerListParams,
} from '@/lib/api/domains/agents-brokers';
import { agentBrokerListQuery } from '../api/agent-broker-queries';
import { agentBrokerColumns } from './agent-broker-columns';

export function AgentBrokerTable({
  params,
  onPageChange,
  onRowClick,
  onEdit,
}: {
  params: AgentBrokerListParams;
  onPageChange: (page: number) => void;
  onRowClick: (agentBroker: AgentBroker) => void;
  onEdit?: (agentBroker: AgentBroker) => void;
}) {
  const query = useQuery(agentBrokerListQuery(params));
  const session = useAuthStore((s) => s.session);
  // The per-row edit is the F04 entity-edit act (Administrator-only), read
  // per-act from the server-sourced capabilities — a `tpabilling_user` (Viewer)
  // holds the read, not this act, so the control is withheld (E02.F04.R5,
  // AC-9311d02c). Courtesy only; the domain gate refuses regardless.
  const showEdit =
    Boolean(onEdit) && canOfferAct(session, 'tpabilling.enrollment.edit');

  return (
    <DataTable<AgentBroker>
      columns={agentBrokerColumns}
      data={query.data?.items ?? []}
      getRowId={(row) => row.id}
      tableId="agents-brokers"
      actions={
        showEdit
          ? (agentBroker) => (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${agentBroker.name}`}
                onClick={() => onEdit?.(agentBroker)}
              >
                <Pencil className="size-4" />
              </Button>
            )
          : undefined
      }
      isLoading={query.isLoading}
      isError={query.isError}
      errorMessage="Failed to load agents and brokers."
      emptyTitle="No agents or brokers found"
      page={query.data?.page ?? params.page ?? 1}
      pageSize={query.data?.pageSize ?? params.pageSize ?? 25}
      total={query.data?.total ?? 0}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
    />
  );
}
